import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import type { ClienteResponse, ParceiroResumoResponse } from '@triade/contracts';
import { useAtualizarCliente, useCliente, useClientes, useCriarCliente } from '@/api/clientes';
import { useCriarFornecedor } from '@/api/fornecedores';
import { conflitoDocumento, useParceiroPorDocumento } from '@/api/parceiros';
import { usePode } from '@/api/me';
import { BarraDeAcoesLista, usePaginarLista } from '@/components/barra-lista';
import { ErroLista } from '@/components/cargos';
import { CodigosExternos } from '@/components/externos';
import { mascaraDocumento } from '@/components/fornecedores/documento';
import { SeloProtheus, camposDoProtheusDe } from '@/components/integracoes-erp/selo-protheus';
import {
  AvisoDocumentoCadastrado,
  ChipsPapeis,
  ParceiroBlocos,
  dadosParceiroAtualizar,
  dadosParceiroCriar,
  schemaFormParceiro,
  valoresDoParceiro,
  valoresNovoParceiro,
  type ParceiroFormValores,
} from '@/components/parceiro';
import { RecordToolbar, SaidaComAlteracoesDialog, useBlockerDirty } from '@/components/record-toolbar';
import { useRolarAoRegistro } from '@/hooks/use-rolar-ao-registro';
import { Input } from '@/components/ui/input';
import { useDebounced } from '@/hooks/use-debounced';
import { BotaoRelatorios } from '@/relatorios';
import { ApiError } from '@/lib/api';
import { formatarContagem } from '@/lib/format';
import { useSessao } from '@/lib/session';
import { cn } from '@/lib/utils';

/** Estado da tela na URL: busca (`q`) e registro aberto (`id`: id real ou 'novo'). */
export interface BuscaClientes {
  q?: string;
  id?: string;
}

/** `validateSearch`: descarta o que nao for valido em vez de quebrar a tela. */
export function lerBuscaClientes(bruto: Record<string, unknown>): BuscaClientes {
  const q = typeof bruto.q === 'string' && bruto.q.trim() ? bruto.q.trim().slice(0, 100) : undefined;
  const id = bruto.id === 'novo' || (typeof bruto.id === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(bruto.id)) ? bruto.id : undefined;
  return { ...(q ? { q } : {}), ...(id ? { id } : {}) };
}

const schemaCliente = schemaFormParceiro('Informe o nome do cliente');

const mensagemApi = (e: unknown) => (e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');

/** Tela "Clientes" (/clientes): lista com busca e registro aberto por `?id=`. Leitura com cliente.ver; gravar com cliente.editar. */
export function ClientesPage({ busca }: { busca: BuscaClientes }) {
  const { empresaId } = useSessao();
  const navigate = useNavigate();
  const { pode } = usePode();
  const podeEditar = pode('cliente.editar');
  const podeFornecedor = pode('custoAquisicao.editar');

  /** `semBloqueio`: troca de registro sem o dialogo "Sair sem salvar?": so a sincronizacao do `q` do debounce e o "Usar o parceiro existente" (descarta o rascunho de proposito). O resto segue o bloqueio. */
  const mudar = (mudanca: Partial<BuscaClientes>, opcoes?: { semBloqueio?: boolean }) => {
    void navigate({ to: '/clientes', search: (anterior) => ({ ...anterior, ...mudanca }), replace: true, ...(opcoes?.semBloqueio ? { ignoreBlocker: true } : {}) });
  };
  const [termo, setTermo] = useState(busca.q ?? '');
  const termoAtrasado = useDebounced(termo.trim(), 300);
  useEffect(() => {
    if (termoAtrasado !== (busca.q ?? '')) mudar({ q: termoAtrasado || undefined }, { semBloqueio: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termoAtrasado]);

  const lista = useClientes({ ...(termoAtrasado ? { q: termoAtrasado } : {}), limit: 200 });
  const clientes = lista.data ?? [];
  const pagClientes = usePaginarLista(clientes);
  const idAberto = busca.id && busca.id !== 'novo' ? busca.id : undefined;
  const registro = useCliente(idAberto);

  let corpo;
  if (empresaId === null) corpo = <Mensagem>Escolha a empresa no menu lateral para ver os clientes.</Mensagem>;
  else if (lista.isError && !lista.data) corpo = <div className="p-4"><ErroLista erro={lista.error} onTentar={() => void lista.refetch()} titulo="Não foi possível carregar os clientes" /></div>;
  else if (!lista.data) corpo = <Mensagem papel="status">Carregando clientes…</Mensagem>;
  else if (clientes.length === 0) {
    corpo = busca.q ? (
      <Mensagem papel="status">
        Nenhum cliente com essa busca.{' '}
        <button type="button" className="font-medium text-primary hover:underline" onClick={() => void navigate({ to: '/clientes', search: {}, replace: true })}>
          Limpar busca
        </button>
      </Mensagem>
    ) : (
      <Mensagem papel="status">
        Nenhum cliente cadastrado.
        {podeEditar && (
          <>
            {' '}
            <Link to="/clientes" search={{ id: 'novo' }} className="font-medium text-primary hover:underline">
              Cadastre o primeiro
            </Link>
            .
          </>
        )}
      </Mensagem>
    );
  } else {
    corpo = (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[320px] border-collapse text-small">
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="px-4 py-2.5 font-medium">Nome</th>
              <th scope="col" className="px-4 py-2.5 font-medium">CPF/CNPJ</th>
            </tr>
          </thead>
          <tbody>
            {pagClientes.visiveis.map((c) => (
              <tr
                key={c.id}
                data-testid="linha-cliente"
                onClick={() => mudar({ id: c.id })}
                className={cn('cursor-pointer border-t border-line-soft hover:bg-surface-muted', busca.id === c.id && 'bg-line-soft')}
              >
                <td className="px-4 py-3 font-semibold">
                  <span className="flex flex-wrap items-center gap-2">
                    {c.nome}
                    {c.doProtheus && <SeloProtheus />}
                  </span>
                </td>
                <td className="tabular px-4 py-3 text-ink-secondary">{c.documento ? mascaraDocumento(c.documento) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  let registroAberto: React.ReactNode = null;
  if (busca.id === 'novo') {
    registroAberto = (
      <ClienteDetalhe
        key="novo"
        cliente={undefined}
        podeEditar={podeEditar}
        podeFornecedor={podeFornecedor}
        onFechar={() => mudar({ id: undefined })}
        onNovo={() => mudar({ id: 'novo' })}
        onAbrir={(id) => mudar({ id }, { semBloqueio: true })}
        onCriado={(id) => mudar({ id })}
      />
    );
  } else if (idAberto) {
    if (registro.isError) registroAberto = <div className="rounded-2xl border border-border bg-card p-4"><ErroLista erro={registro.error} onTentar={() => void registro.refetch()} titulo="Não foi possível carregar o cliente" /></div>;
    else if (registro.data)
      registroAberto = (
        <ClienteDetalhe
          key={registro.data.id}
          cliente={registro.data}
          podeEditar={podeEditar}
          podeFornecedor={podeFornecedor}
          onFechar={() => mudar({ id: undefined })}
          onNovo={() => mudar({ id: 'novo' })}
          onAbrir={(id) => mudar({ id }, { semBloqueio: true })}
          onCriado={(id) => mudar({ id })}
        />
      );
  }

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-small text-ink-muted">
            Vender <span aria-hidden="true">/</span> Clientes
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Clientes</h1>
        </div>
      </div>

      <div className="relative max-w-[360px]">
        <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-muted" />
        <Input
          type="search"
          aria-label="Buscar cliente"
          placeholder="Buscar por nome ou documento"
          value={termo}
          onChange={(e) => setTermo(e.target.value)}
          className="pl-9"
        />
      </div>

      <section aria-label="Lista de clientes" aria-busy={lista.isFetching} className="rounded-2xl border border-border bg-card">
        {corpo}
        <div className="border-t border-border px-4 py-3 text-small text-ink-secondary" data-testid="contagem-clientes">
          {lista.data ? `${formatarContagem(clientes.length)} cliente${clientes.length === 1 ? '' : 's'} na lista` : ' '}
        </div>
      </section>

      {registroAberto}

      {/* Com o registro aberto a RecordToolbar (Salvar) ocupa o rodapé: a barra da lista ficaria por cima. */}
      {!busca.id && (
        <BarraDeAcoesLista
          novo={podeEditar ? { rotulo: 'Novo cliente', onClick: () => mudar({ id: 'novo' }) } : undefined}
          paginacao={lista.data ? pagClientes.paginacao : undefined}
          relatorios={<BotaoRelatorios tela="clientes" params={{ q: busca.q }} />}
        />
      )}
    </div>
  );
}

function ClienteDetalhe({
  cliente,
  podeEditar,
  podeFornecedor,
  onFechar,
  onNovo,
  onAbrir,
  onCriado,
}: {
  cliente: ClienteResponse | undefined;
  podeEditar: boolean;
  /** custoAquisicao.editar: pode tornar o cliente tambem fornecedor. */
  podeFornecedor: boolean;
  onFechar: () => void;
  onNovo: () => void;
  onAbrir: (id: string) => void;
  onCriado: (id: string) => void;
}) {
  const criar = useCriarCliente();
  const rolagem = useRolarAoRegistro<HTMLElement>();
  const atualizar = useAtualizarCliente();
  const criarFornecedor = useCriarFornecedor();
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  /** Documento ja cadastrado para outro parceiro (409 ao gravar): mostra o aviso com "usar o parceiro existente". */
  const [conflito, setConflito] = useState<{ documento: string; nome: string } | null>(null);
  const resumoConflito: ParceiroResumoResponse | null = useParceiroPorDocumento(conflito?.documento ?? null).data?.parceiro ?? null;
  /** Fornecedor criado a partir deste cliente ("Também é fornecedor"). */
  const [fornecedorCriadoId, setFornecedorCriadoId] = useState<string | null>(null);
  const [erroPapel, setErroPapel] = useState<string | null>(null);
  /** Cliente recem-criado: a navegacao para ?id= acontece depois do render (o bloqueio de saida ja nao vale). */
  const [criadoId, setCriadoId] = useState<string | null>(null);
  const base = cliente ? valoresDoParceiro(cliente) : valoresNovoParceiro();
  const form = useForm<ParceiroFormValores>({ resolver: zodResolver(schemaCliente), defaultValues: base, mode: 'onTouched' });
  const { isDirty, dirtyFields, errors } = form.formState;
  const bloqueio = useBlockerDirty(isDirty && criadoId === null);
  useEffect(() => {
    if (criadoId) onCriado(criadoId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criadoId]);
  const salvando = criar.isPending || atualizar.isPending;
  const alterados = Object.keys(dirtyFields).length;

  async function gravar(v: ParceiroFormValores) {
    setErroSalvar(null);
    setConflito(null);
    try {
      if (cliente) {
        const gravado = await atualizar.mutateAsync({ id: cliente.id, ...dadosParceiroAtualizar(v) });
        form.reset(valoresDoParceiro(gravado));
        setSalvoEm(new Date());
      } else {
        const criado = await criar.mutateAsync(dadosParceiroCriar(v));
        form.reset(valoresDoParceiro(criado));
        setSalvoEm(new Date());
        setCriadoId(criado.id);
      }
    } catch (e) {
      const dup = conflitoDocumento(e);
      if (dup) setConflito({ documento: v.documento, nome: dup.nome });
      else setErroSalvar(mensagemApi(e));
    }
  }

  /** "Usar o parceiro existente": se ja e cliente, abre o cliente; senao cria o cliente com o parceiro existente e abre. */
  async function usarExistente(r: ParceiroResumoResponse) {
    setErroSalvar(null);
    setConflito(null);
    if (r.clienteId) {
      onAbrir(r.clienteId);
      return;
    }
    try {
      const criado = await criar.mutateAsync({ parceiroId: r.id });
      setCriadoId(criado.id);
    } catch (e) {
      setErroSalvar(mensagemApi(e));
    }
  }

  async function tambemFornecedor() {
    if (!cliente) return;
    setErroPapel(null);
    try {
      const fornecedor = await criarFornecedor.mutateAsync({ parceiroId: cliente.parceiroId });
      setFornecedorCriadoId(fornecedor.id);
    } catch (e) {
      setErroPapel(mensagemApi(e));
    }
  }

  return (
    <>
      <section ref={rolagem} aria-label="Dados do cliente" className="rounded-2xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-body font-semibold">{cliente ? cliente.nome : 'Novo cliente'}</h2>
          {cliente?.doProtheus && <SeloProtheus />}
        </div>
        {!podeEditar && (
          <p role="status" className="mt-2 rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
            <b>Somente leitura.</b> Seu acesso permite consultar os clientes, não alterá-los.
          </p>
        )}
        {cliente && (
          <div className="mt-4 flex flex-col gap-3">
            <ChipsPapeis papeis={cliente.papeis} />
            {podeFornecedor && !cliente.papeis.includes('FORNECEDOR') && fornecedorCriadoId === null && (
              <div>
                <button
                  type="button"
                  className="rounded-md border border-input px-3 py-1.5 text-small font-medium text-primary hover:bg-surface-muted disabled:opacity-50"
                  onClick={() => void tambemFornecedor()}
                  disabled={criarFornecedor.isPending}
                >
                  Também é fornecedor
                </button>
              </div>
            )}
            {fornecedorCriadoId && (
              <p role="status" className="text-small text-ink-body">
                Agora também é fornecedor.{' '}
                <Link to="/cadastros/fornecedores" search={{ fornecedor: fornecedorCriadoId }} className="font-medium text-primary hover:underline">
                  Abrir o fornecedor
                </Link>
              </p>
            )}
            {erroPapel && (
              <p role="alert" className="text-caption text-danger-ink">
                {erroPapel}
              </p>
            )}
          </div>
        )}
        <fieldset className="mt-4">
          <ParceiroBlocos
            form={form}
            papel="CLIENTE"
            parceiroId={cliente?.parceiroId}
            camposDoProtheus={camposDoProtheusDe('Cliente', cliente?.doProtheus)}
            onUsarExistente={(r) => void usarExistente(r)}
            desabilitado={!podeEditar}
          />
        </fieldset>
        {errors.nome && (
          <p role="alert" className="mt-3 text-caption text-danger-ink">
            {errors.nome.message}
          </p>
        )}
        {conflito && (
          <AvisoDocumentoCadastrado nome={conflito.nome} onUsar={() => resumoConflito && void usarExistente(resumoConflito)} desabilitado={!resumoConflito} />
        )}
        {erroSalvar && (
          <p role="alert" className="mt-3 text-caption text-danger-ink">
            {erroSalvar}
          </p>
        )}
        {cliente && (
          <div className="mt-5 border-t border-line-soft pt-4">
            <CodigosExternos entidade="clientes" id={cliente.id} editavel={podeEditar} colecaoFixa="clientes" />
          </div>
        )}
      </section>

      {podeEditar && (
        <RecordToolbar
          dirty={isDirty}
          saving={salvando}
          savedAt={salvoEm}
          erro={erroSalvar}
          novo={!cliente}
          canPrev={false}
          canNext={false}
          alteradosCount={isDirty ? alterados : undefined}
          onList={onFechar}
          onSave={() => void form.handleSubmit(gravar, () => setErroSalvar('Corrija os campos destacados'))()}
          onNew={onNovo}
          onUndo={() => {
            form.reset(base);
            setErroSalvar(null);
            setConflito(null);
          }}
        />
      )}
      <SaidaComAlteracoesDialog {...bloqueio} />
    </>
  );
}

function Mensagem({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}
